/**
 * @fileoverview Hook de Sincronização - DRE ↔ ValuationEngine
 * @module hooks/useDREValuation
 * 
 * Hook React que sincroniza automaticamente dados da DRE editável
 * com o ValuationEngine, recalculando projeções quando DRE muda.
 * 
 * Funcionalidades:
 * - Carrega DRE automaticamente
 * - Converte DRE → HistoricalData
 * - Recalcula quando DRE muda (com debounce)
 * - Indicador de sincronização
 * - Forçar recálculo manual
 * 
 * @example
 * ```tsx
 * function ValuationPage() {
 *   const { 
 *     historicalData, 
 *     isLoading, 
 *     isSynced, 
 *     lastSyncAt,
 *     recalculate 
 *   } = useDREValuation();
 * 
 *   // Usar historicalData no ValuationEngine
 *   const engine = new ValuationEngine(historicalData, ...);
 *   const result = engine.calculate();
 * }
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDREStore } from '../stores/dreStore';
import { dreToHistoricalData, hasValidDataForValuation, HistoricalData } from '../lib/dre/adapters';
import toast from 'react-hot-toast';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Estado retornado pelo hook useDREValuation
 */
export interface UseDREValuationResult {
  /** Dados históricos no formato do ValuationEngine */
  historicalData: HistoricalData[];
  
  /** Indica se está carregando dados da DRE */
  isLoading: boolean;
  
  /** Indica se DRE está sincronizada com valuation */
  isSynced: boolean;
  
  /** Timestamp da última sincronização */
  lastSyncAt: Date | null;
  
  /** Versão ativa da DRE (master ou editável) */
  activeVersion: 'master' | 'editable';
  
  /** Forçar recálculo manual */
  recalculate: () => void;
  
  /** Mudar versão ativa (master ou editável) */
  switchVersion: (version: 'master' | 'editable') => void;
}

/**
 * Opções para configurar o hook
 */
export interface UseDREValuationOptions {
  /** Tempo de debounce em ms (padrão: 500ms) */
  debounceMs?: number;
  
  /** Se true, carrega versão master ao invés da editável */
  useMasterVersion?: boolean;
  
  /** Se true, mostra toast notifications */
  showNotifications?: boolean;
  
  /** Callback chamado após recálculo */
  onRecalculate?: (historicalData: HistoricalData[]) => void;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

/**
 * Hook para sincronizar DRE editável com ValuationEngine
 * VERSÃO SIMPLIFICADA - sem loading complexo
 * 
 * @param options - Opções de configuração
 * @returns Estado e funções de controle
 */
export function useDREValuation(
  options: UseDREValuationOptions = {}
): UseDREValuationResult {
  const {
    useMasterVersion = false,
    showNotifications = false,
  } = options;

  // ========================================
  // Estado do Zustand (leitura direta)
  // ========================================
  const masterData = useDREStore(state => state.masterData);
  const dreData = useDREStore(state => state.dreData);
  const isLoadingDRE = useDREStore(state => state.isLoading);

  // ========================================
  // Estado Local
  // ========================================
  const [isSynced, setIsSynced] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [activeVersion, setActiveVersion] = useState<'master' | 'editable'>(
    useMasterVersion ? 'master' : 'editable'
  );

  // ========================================
  // Dados convertidos (memoizados)
  // ========================================
  const historicalData = useMemo(() => {
    const currentData = activeVersion === 'master' ? masterData : dreData;
    
    console.log('[useDREValuation] 🔄 useMemo recalculando para versão:', activeVersion, {
      usandoMaster: activeVersion === 'master',
      hasMasterData: !!masterData,
      hasDreData: !!dreData,
    });
    
    if (!currentData || !hasValidDataForValuation(currentData)) {
      console.log('[useDREValuation] ⚠️  Sem dados válidos para versão:', activeVersion);
      return [];
    }

    try {
      const converted = dreToHistoricalData(currentData);
      console.log('[useDREValuation] ✅ Dados convertidos:', converted.length, 'anos', `(versão: ${activeVersion})`);
      return converted;
    } catch (error) {
      console.error('[useDREValuation] ❌ Erro ao converter:', error);
      return [];
    }
  }, [masterData, dreData, activeVersion]);

  // ========================================
  // Efeito: Atualizar estado quando dados mudam
  // ========================================
  useEffect(() => {
    if (historicalData.length > 0) {
      setIsSynced(true);
      setLastSyncAt(new Date());
    } else {
      setIsSynced(false);
    }
  }, [historicalData]);

  // ========================================
  // Função: Recálculo Manual
  // ========================================
  const recalculate = useCallback(() => {
    // Como historicalData é calculado via useMemo, 
    // apenas atualizamos o timestamp para indicar recálculo manual
    setLastSyncAt(new Date());
    
    if (showNotifications && historicalData.length > 0) {
      toast.success('Dados recalculados');
    } else if (showNotifications) {
      toast.error('Nenhum dado válido para recalcular');
    }
  }, [historicalData, showNotifications]);

  // ========================================
  // Função: Mudar Versão Ativa
  // ========================================
  const switchVersion = useCallback((version: 'master' | 'editable') => {
    console.log('[useDREValuation] 🔄 switchVersion chamado:', {
      versaoAnterior: activeVersion,
      novaVersao: version,
    });
    
    setActiveVersion(version);
    
    if (showNotifications) {
      const versionName = version === 'master' ? 'Master (Original)' : 'Editável';
      toast.success(`Usando versão: ${versionName}`, { duration: 2000 });
    }
    
    console.log('[useDREValuation] ✅ activeVersion atualizado para:', version);
  }, [activeVersion, showNotifications]);

  // ========================================
  // Retorno
  // ========================================
  return {
    historicalData,
    isLoading: isLoadingDRE, // Usar apenas loading do store
    isSynced,
    lastSyncAt,
    activeVersion,
    recalculate,
    switchVersion,
  };
}

// ============================================================================
// HOOK AUXILIAR: Status de Sincronização
// ============================================================================

/**
 * Estado de sincronização simplificado
 */
export type SyncStatus = 'synced' | 'calculating' | 'out-of-sync' | 'error';

/**
 * Hook simplificado para apenas verificar status de sincronização
 * Útil para badges e indicadores visuais
 * 
 * @example
 * ```tsx
 * function SyncBadge() {
 *   const status = useDRESyncStatus();
 *   
 *   return (
 *     <Chip 
 *       label={status} 
 *       color={status === 'synced' ? 'success' : 'warning'}
 *     />
 *   );
 * }
 * ```
 */
export function useDRESyncStatus(): SyncStatus {
  const { isSynced, isLoading } = useDREValuation({ 
    showNotifications: false,
    debounceMs: 300, // Mais rápido para badges
  });

  if (isLoading) return 'calculating';
  if (isSynced) return 'synced';
  return 'out-of-sync';
}

// ============================================================================
// HOOK AUXILIAR: Comparação com Master
// ============================================================================

/**
 * Resultado da comparação entre versão editável e master
 */
export interface DREComparisonResult {
  /** Diferença percentual no valor final */
  finalValueDelta: number;
  
  /** Diferença absoluta em receita total */
  revenueDelta: number;
  
  /** Diferença absoluta em EBITDA total */
  ebitdaDelta: number;
  
  /** Número de anos modificados */
  yearsChanged: number;
  
  /** Se houve mudanças significativas (>5%) */
  hasSignificantChanges: boolean;
}

/**
 * Hook para comparar versão editável com master
 * 
 * @example
 * ```tsx
 * function ComparisonPanel() {
 *   const comparison = useDREComparison();
 *   
 *   if (!comparison) return null;
 *   
 *   return (
 *     <Alert severity={comparison.hasSignificantChanges ? 'warning' : 'info'}>
 *       Mudanças: {comparison.yearsChanged} anos
 *       Delta valor: {comparison.finalValueDelta}%
 *     </Alert>
 *   );
 * }
 * ```
 */
export function useDREComparison(): DREComparisonResult | null {
  const masterData = useDREStore(state => state.masterData);
  const dreData = useDREStore(state => state.dreData);

  const [comparison, setComparison] = useState<DREComparisonResult | null>(null);

  useEffect(() => {
    if (!masterData || !dreData) {
      setComparison(null);
      return;
    }

    try {
      // Converter ambas versões
      const masterHistorical = dreToHistoricalData(masterData);
      const editableHistorical = dreToHistoricalData(dreData);

      // Calcular diferenças
      let revenueDelta = 0;
      let ebitdaDelta = 0;
      let yearsChanged = 0;

      masterHistorical.forEach(masterYear => {
        const editableYear = editableHistorical.find(y => y.year === masterYear.year);
        
        if (editableYear) {
          const masterRevenue = masterYear.setima.receita + masterYear.studio555.receita;
          const editableRevenue = editableYear.setima.receita + editableYear.studio555.receita;
          
          const masterEbitda = masterYear.setima.ebitda + masterYear.studio555.ebitda;
          const editableEbitda = editableYear.setima.ebitda + editableYear.studio555.ebitda;

          revenueDelta += (editableRevenue - masterRevenue);
          ebitdaDelta += (editableEbitda - masterEbitda);

          // Considerar "mudado" se diferença > 1%
          if (Math.abs((editableRevenue - masterRevenue) / masterRevenue) > 0.01) {
            yearsChanged++;
          }
        }
      });

      // Calcular delta percentual (simplificado)
      const totalMasterRevenue = masterHistorical.reduce(
        (sum, y) => sum + y.setima.receita + y.studio555.receita,
        0
      );
      const finalValueDelta = totalMasterRevenue > 0 
        ? (revenueDelta / totalMasterRevenue) * 100 
        : 0;

      setComparison({
        finalValueDelta,
        revenueDelta,
        ebitdaDelta,
        yearsChanged,
        hasSignificantChanges: Math.abs(finalValueDelta) > 5,
      });
    } catch (error) {
      console.error('Erro ao comparar versões:', error);
      setComparison(null);
    }
  }, [masterData, dreData]);

  return comparison;
}
